/** The notification centre: everything we pushed or emailed, unread first by day, each opening its screen. */
import { Button, EmptyState, Icon, List, Row, type IconName } from '@ff/app-ui';
import type { JSX } from 'preact';
import { invalidate, send, setCached, useQuery } from '../lib/data.ts';
import { rowTime } from '../lib/format.ts';
import { useStore } from '../lib/store.ts';
import { tz } from '../lib/ui.ts';
import { S } from '@ff/app-ui/copy';
import { Loaded, Screen, Section } from '../ui/common.tsx';

const EVENT_ICON: Record<string, IconName> = {
  'lead.new': 'leads', 'review.new_high': 'star', 'review.new_low': 'star', 'approval.needed': 'check', 'request.update': 'inbox', 'report.ready': 'file',
  'onboarding.reminder': 'clock', 'payment.failed': 'card', 'security.new_device': 'shield', 'site.down': 'alert', 'summary.weekly': 'calendar',
};

export function NotificationsScreen(): JSX.Element {
  const zone = useStore(tz);
  const q = useQuery('notifications', { query: { limit: 30 } });
  const markAll = async () => {
    await send('notificationsRead', { body: { all: true } }).catch(() => undefined);
    setCached('notifications', { query: { limit: 30 } }, (d) => (d ? { ...d, unread: 0, items: d.items.map((n) => ({ ...n, readAt: n.readAt ?? new Date().toISOString() })) } : d));
  };
  const open = (id: string) => void send('notificationsRead', { body: { ids: [id] } }).then(() => invalidate('notifications'), () => undefined);
  return (
    <Screen
      title={S.notifications.title}
      query={q}
      actions={
        q.data && q.data.unread > 0 ? (
          <Button size="sm" variant="secondary" icon="check" onClick={() => void markAll()}>
            {S.notifications.markAll}
          </Button>
        ) : null
      }
    >
      <Loaded q={q}>
        {(d) => {
          if (!d.items.length) return <EmptyState title={S.notifications.title} body={S.notifications.empty} />;
          const today = new Date().toDateString();
          const groups: [string, typeof d.items][] = [
            [S.notifications.today, d.items.filter((n) => new Date(n.createdAt).toDateString() === today)],
            [S.notifications.earlier, d.items.filter((n) => new Date(n.createdAt).toDateString() !== today)],
          ];
          return (
            <div class="stack" data-gap="s">
              {groups
                .filter(([, items]) => items.length)
                .map(([title, items]) => (
                  <Section key={title} title={title}>
                    <List>
                      {items.map((n) => (
                        <li key={n.id} onClick={() => !n.readAt && open(n.id)}>
                          <Row href={n.link} unread={!n.readAt} unreadLabel={S.app.new} lead={n.readAt ? <Icon name={EVENT_ICON[n.event] ?? 'bell'} size={22} /> : undefined} title={n.title} meta={n.body} time={rowTime(n.createdAt, zone)} />
                        </li>
                      ))}
                    </List>
                  </Section>
                ))}
              <a class="section-link" href="/account/notifications">
                <Icon name="settings" size={20} /> {S.notifications.settings}
              </a>
            </div>
          );
        }}
      </Loaded>
    </Screen>
  );
}
